import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ReactPlayer from 'react-player';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VideoPlayerPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchVideo();
  }, [id, token]);

  const fetchVideo = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/videos/${id}`, { headers });
      setVideo(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        setError('premium');
      } else if (error.response?.status === 404) {
        setError('notfound');
      } else {
        setError('general');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Check if it's a short (duration parsing)
  const isShort = video && video.duration && (
    video.duration.includes('PT') && 
    !video.duration.includes('H') && 
    (video.duration.match(/(\d+)M/)?.[1] || 0) < 1
  );

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <Navbar />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        {loading ? (
          <div className="animate-pulse">
            <div className="bg-slate-800 aspect-video rounded-sm mb-6"></div>
            <div className="h-8 bg-slate-800 rounded mb-4 w-3/4"></div>
            <div className="h-4 bg-slate-800 rounded w-1/4"></div>
          </div>
        ) : error === 'premium' ? (
          <div className="text-center py-24">
            <Lock className="w-16 h-16 text-red-600 mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>Premium Content</h2>
            <p className="text-xl text-slate-400 mb-8" style={{ fontFamily: 'Inter, sans-serif' }}>
              This video is only available to premium subscribers.
            </p>
            <Link to="/pricing">
              <Button className="bg-red-600 hover:bg-red-700 text-white" data-testid="upgrade-button">
                Upgrade to Premium
              </Button>
            </Link>
          </div>
        ) : error === 'notfound' ? (
          <div className="text-center py-24">
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>Video Not Found</h2>
            <p className="text-xl text-slate-400 mb-8" style={{ fontFamily: 'Inter, sans-serif' }}>
              The video you're looking for doesn't exist.
            </p>
            <Link to="/videos">
              <Button className="bg-red-600 hover:bg-red-700 text-white">
                Browse Videos
              </Button>
            </Link>
          </div>
        ) : error ? (
          <div className="text-center py-24">
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>Error Loading Video</h2>
            <p className="text-xl text-slate-400 mb-8" style={{ fontFamily: 'Inter, sans-serif' }}>
              Something went wrong. Please try again later.
            </p>
            <Link to="/videos">
              <Button className="bg-red-600 hover:bg-red-700 text-white">
                Browse Videos
              </Button>
            </Link>
          </div>
        ) : video && (
          <>
            {isShort ? (
              <div className="text-center py-12">
                <div className="aspect-video bg-slate-900 rounded-sm overflow-hidden mb-6 border border-slate-800 flex items-center justify-center">
                  <div>
                    <p className="text-slate-400 mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
                      This is a YouTube Short. Shorts can't be embedded but you can watch it on YouTube.
                    </p>
                    <a 
                      href={`https://www.youtube.com/shorts/${video.youtube_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button className="bg-red-600 hover:bg-red-700 text-white">
                        Watch on YouTube
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            ) : (
              <div data-testid="video-player" className="aspect-video bg-slate-900 rounded-sm overflow-hidden mb-6 border border-slate-800">
                <ReactPlayer
                  url={`https://www.youtube.com/watch?v=${video.youtube_id}`}
                  width="100%"
                  height="100%"
                  controls
                  playing={false}
                  config={{
                    youtube: {
                      playerVars: {
                        autoplay: 0,
                        modestbranding: 1,
                        rel: 0
                      }
                    }
                  }}
                />
              </div>
            )}
            
            <div>
              <h1 
                data-testid="video-title"
                className="text-3xl sm:text-4xl font-bold mb-4"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                {video.title}
              </h1>
              <p className="text-slate-400 mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
                Published on {formatDate(video.published_at)}
              </p>
              <div className="p-6 bg-slate-900 rounded-sm border border-slate-800">
                <p className="text-slate-300 whitespace-pre-wrap" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {video.description}
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VideoPlayerPage;
